import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTokens } from 'theme';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'i18n/format';

/**
 * Hand-rolled daily bar chart: one bar per point, its `part` (failures,
 * errors) stacked on top in red. `series` is dense and ordered, each point
 * carrying an ISO `date`.
 */
export default function BarChart({
  series,
  total,
  part = () => 0,
  tooltip,
  emptyText,
  totalLabel,
  partLabel,
  height = 120,
}) {
  useTranslation();
  const t = useTokens();
  const max = Math.max(1, ...series.map(total));
  const hasData = series.some((d) => total(d) > 0);

  if (!hasData) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: t.inkMuted }}>
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height }}>
        {series.map((d) => {
          const value = total(d);
          const pct = (value / max) * 100;
          const partPct = value ? (part(d) / value) * 100 : 0;
          return (
            <Tooltip key={d.date} arrow title={tooltip(d)}>
              <Box sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: `${Math.max(pct, 2)}%`,
                    minHeight: 2,
                    bgcolor: t.positive,
                    border: `1px solid ${t.positive}`,
                    borderRadius: '2px 2px 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'opacity 120ms ease',
                    '&:hover': { opacity: 0.75 },
                  }}
                >
                  {partPct > 0 && (
                    <Box sx={{ width: '100%', height: `${partPct}%`, bgcolor: t.negative, borderRadius: '2px 2px 0 0' }} />
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{ fontSize: '0.65rem', color: t.inkMuted }}>{formatDate(series[0]?.date, { month: 'short', day: 'numeric' })}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: t.inkMuted }}>{formatDate(series[series.length - 1]?.date, { month: 'short', day: 'numeric' })}</Typography>
      </Box>
      {(totalLabel || partLabel) && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {totalLabel && <Legend color={t.positive} label={totalLabel} />}
          {partLabel && <Legend color={t.negative} label={partLabel} />}
        </Box>
      )}
    </>
  );
}

function Legend({ color, label }) {
  const t = useTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px' }} />
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: t.inkMuted }}>{label}</Typography>
    </Box>
  );
}
